import { createFileRoute, Link, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { SiteFooter, SiteNav } from "@/components/SiteChrome";
import {
  createEssay,
  deleteEssay,
  getEssayById,
  listAllEssays,
  setEssayStatus,
  updateEssay,
} from "@/lib/essays.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({
    meta: [
      { title: "Admin — Akram Fortas" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminPage,
});

function slugify(input: string) {
  return input
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120);
}

function AdminPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const listFn = useServerFn(listAllEssays);
  const getFn = useServerFn(getEssayById);
  const createFn = useServerFn(createEssay);
  const updateFn = useServerFn(updateEssay);
  const statusFn = useServerFn(setEssayStatus);
  const deleteFn = useServerFn(deleteEssay);

  const list = useQuery({ queryKey: ["essays", "all"], queryFn: () => listFn() });

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [draft, setDraft] = useState({
    slug: "",
    title: "",
    subtitle: "",
    category: "",
    body_md: "",
  });
  const [busy, setBusy] = useState(false);

  const selected = useQuery({
    queryKey: ["essays", "one", selectedId],
    queryFn: () => getFn({ data: { id: selectedId! } }),
    enabled: !!selectedId,
  });

  useEffect(() => {
    if (selected.data) {
      setDraft({
        slug: selected.data.slug ?? "",
        title: selected.data.title ?? "",
        subtitle: selected.data.subtitle ?? "",
        category: selected.data.category ?? "",
        body_md: selected.data.body_md ?? "",
      });
    }
  }, [selected.data]);

  function resetForm() {
    setSelectedId(null);
    setDraft({ slug: "", title: "", subtitle: "", category: "", body_md: "" });
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      if (selectedId) {
        await updateFn({
          data: {
            id: selectedId,
            slug: draft.slug,
            title: draft.title,
            subtitle: draft.subtitle || null,
            category: draft.category || null,
            body_md: draft.body_md,
          },
        });
        toast.success("Saved.");
      } else {
        const row = await createFn({
          data: {
            slug: draft.slug || slugify(draft.title),
            title: draft.title,
            subtitle: draft.subtitle || null,
            category: draft.category || null,
            body_md: draft.body_md,
          },
        });
        toast.success("Draft created.");
        setSelectedId(row?.id ?? null);
      }
      await queryClient.invalidateQueries({ queryKey: ["essays"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  async function onPublishToggle(id: string, current: "draft" | "published") {
    setBusy(true);
    try {
      await statusFn({
        data: { id, status: current === "published" ? "draft" : "published" },
      });
      await queryClient.invalidateQueries({ queryKey: ["essays"] });
      toast.success(current === "published" ? "Unpublished." : "Published.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Publish failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: string) {
    if (!confirm("Delete this essay? This cannot be undone.")) return;
    setBusy(true);
    try {
      await deleteFn({ data: { id } });
      if (selectedId === id) resetForm();
      await queryClient.invalidateQueries({ queryKey: ["essays"] });
      toast.success("Deleted.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    } finally {
      setBusy(false);
    }
  }

  async function onSignOut() {
    await queryClient.cancelQueries();
    queryClient.clear();
    await supabase.auth.signOut();
    router.navigate({ to: "/", replace: true });
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteNav />
      <main className="container-prose pt-20 pb-16 md:pt-28">
        <div className="flex items-baseline justify-between gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Gardener's bench
            </p>
            <h1 className="mt-4 font-serif text-4xl text-foreground md:text-5xl">
              Essays & drafts
            </h1>
          </div>
          <button
            onClick={onSignOut}
            className="text-sm text-muted-foreground link-quiet underline underline-offset-4"
          >
            Sign out
          </button>
        </div>

        <section className="mt-12 grid gap-10 md:grid-cols-[minmax(0,20rem)_1fr]">
          <aside>
            <div className="flex items-baseline justify-between">
              <h2 className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Patches
              </h2>
              <button
                onClick={resetForm}
                className="text-xs text-muted-foreground link-quiet underline underline-offset-4"
              >
                + New
              </button>
            </div>
            <ul className="mt-4 divide-y hairline border-y">
              {list.isLoading && <li className="py-3 text-sm text-muted-foreground">Loading…</li>}
              {list.data?.length === 0 && (
                <li className="py-3 text-sm text-muted-foreground">No essays yet.</li>
              )}
              {list.data?.map((e) => (
                <li key={e.id} className="py-3">
                  <button
                    onClick={() => setSelectedId(e.id)}
                    className={`block w-full text-left ${
                      selectedId === e.id ? "text-foreground" : "text-muted-foreground"
                    }`}
                  >
                    <span className="font-serif text-lg">{e.title || "(untitled)"}</span>
                    <span className="ml-2 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
                      {e.status}
                    </span>
                  </button>
                  <div className="mt-1 flex gap-4 text-xs text-muted-foreground">
                    <button
                      onClick={() => onPublishToggle(e.id, e.status as "draft" | "published")}
                      disabled={busy}
                      className="link-quiet underline underline-offset-4"
                    >
                      {e.status === "published" ? "Unpublish" : "Publish"}
                    </button>
                    <button
                      onClick={() => onDelete(e.id)}
                      disabled={busy}
                      className="link-quiet underline underline-offset-4"
                    >
                      Delete
                    </button>
                    {e.status === "published" && (
                      <Link
                        to="/garden/$slug"
                        params={{ slug: e.slug }}
                        className="link-quiet underline underline-offset-4"
                      >
                        View ↗
                      </Link>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </aside>

          <form onSubmit={onSave} className="space-y-6">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Title">
                <input
                  required
                  value={draft.title}
                  onChange={(ev) => setDraft({ ...draft, title: ev.target.value })}
                  className="w-full border-b hairline bg-transparent py-2 font-serif text-2xl text-foreground focus:border-foreground focus:outline-none"
                />
              </Field>
              <Field label="Slug">
                <input
                  required
                  placeholder={slugify(draft.title)}
                  value={draft.slug}
                  onChange={(ev) => setDraft({ ...draft, slug: ev.target.value })}
                  className="w-full border-b hairline bg-transparent py-2 font-mono text-sm text-foreground focus:border-foreground focus:outline-none"
                />
              </Field>
              <Field label="Subtitle">
                <input
                  value={draft.subtitle}
                  onChange={(ev) => setDraft({ ...draft, subtitle: ev.target.value })}
                  className="w-full border-b hairline bg-transparent py-2 text-foreground focus:border-foreground focus:outline-none"
                />
              </Field>
              <Field label="Category">
                <input
                  list="essay-categories"
                  value={draft.category}
                  onChange={(ev) => setDraft({ ...draft, category: ev.target.value })}
                  className="w-full border-b hairline bg-transparent py-2 text-foreground focus:border-foreground focus:outline-none"
                />
                <datalist id="essay-categories">
                  <option value="Marketing" />
                  <option value="Business" />
                  <option value="Experiments" />
                  <option value="Travel" />
                  <option value="Life" />
                </datalist>
              </Field>
            </div>
            <Field label="Body (markdown)">
              <textarea
                value={draft.body_md}
                onChange={(ev) => setDraft({ ...draft, body_md: ev.target.value })}
                rows={20}
                className="w-full border hairline bg-transparent p-4 font-mono text-sm text-foreground focus:border-foreground focus:outline-none"
              />
            </Field>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={busy}
                className="inline-flex items-center justify-center rounded-full bg-foreground px-6 py-2.5 text-sm text-background transition-opacity hover:opacity-90 disabled:opacity-50"
              >
                {selectedId ? "Save changes" : "Create draft"}
              </button>
              {selectedId && (
                <button
                  type="button"
                  onClick={resetForm}
                  className="text-sm text-muted-foreground link-quiet underline underline-offset-4"
                >
                  Cancel
                </button>
              )}
            </div>
          </form>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
